import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './JobDetailsAddUnknown.less';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { push } from 'redux-first-history';

import { notificationActions } from '../../../../redux/notification';
import { unknownReadingActions } from '../../../../redux/unknownReading';
import { applicationSelectors } from '../../../../redux/application';
import { loginSelectors } from '../../../../redux/login';
import { NotificationType } from '../../../MainScreen/NotificationWrapper/NotificationWrapper';
import { UnknownMeterReadingResultType } from '../../../../redux/unknownReading/unknownReading.types';

import BasicScreen from '../../../common/BasicScreen/BasicScreen';
import Card from '../../../../components/envago/Card/Card';
import LabeledList from '../../../../components/envago/LabeledList/LabeledList';
import OptionalLabel from '../../../../components/envago/OptionalLabel/OptionalLabel';
import Input from '../../../../components/envago/Input/Input';
import Toolbar from '../../../../components/envago/Toolbar/Toolbar';
import MeterReadingInput from '../../../../components/envago/MeterReadingInput/MeterReadingInput';
import DatePicker from '../../../../components/envago/DatePicker/DatePicker';
import JobComment from '../../../../components/envago/JobComment/JobComment';
import PopConfirmButton from '../../../../components/envago/PopConfirmButton/PopConfirmButton';
import Icon from '../../../../components/envago/Icon/Icon';
import Dropdown from '../../../../components/envago/Dropdown/Dropdown';
import { useUnsavedChangesGuard } from '../../../common/UnsavedChangesGuard/UnsavedChangesGuard';
import {
    buildUnknownMeterReadingSnapshot,
    isUnknownMeterReadingDirty,
    resolveUnknownMeterReadingBaselineAfterAccountAutoSelect,
} from './JobDetailsAddUnknown.unsavedChanges';
import { InvalidReadingImagePayloadError, toReadingImagePayload } from '../../../../helper/ReadingImagePayloadHelper';
import { resolveJobPhotoFlow, resolveReadingImagePrimary } from '../JobDetails/jobPhotoFlow';
import JobDetailsReadingImageEntry from '../JobDetails/components/JobDetailsReadingImageEntry';
import { isMobilePlatform } from '../../../../helper/PlatformHelper';
import { transValue } from '../../../../helper/TransHelper';
import { MAX_READING_IMAGES } from '../../../../constants/ReadingImageLimits';
import { HorizontalBase64Image } from '../../../../components/envago/HorizontalBase64Image';
import { RemoteResult } from '../../../common/RemoteResultCommand/index.types';

interface UnknownMeterReadingImage {
    imageBase64: string;
    displayCrop?: string;
}

interface UnknownMeterReadingStateType {
    meterNumber: string;
    readingValue: string;
    images: UnknownMeterReadingImage[];
    readingDate: Date;
    comment?: string;
}

interface JobDetailsAddUnknownProps {
    className?: string;
}

const JobDetailsAddUnknown = ({ className }: JobDetailsAddUnknownProps) => {
    const [hasFocus, setHasFocus] = useState({
        meterNumberInput: false,
        readingValueInput: false,
    });
    const [accountId, setAccountId] = useState('');
    const [instanceKey, setInstanceKey] = useState<number>(0);
    const [unknownReading, setUnknownReading] = useState<UnknownMeterReadingStateType>({
        meterNumber: '',
        readingValue: '',
        images: [],
        readingDate: dayjs().toDate(),
        comment: undefined,
    });
    const initialSnapshotRef = useRef(
        buildUnknownMeterReadingSnapshot({
            accountId: '',
            unknownReading: { ...unknownReading, images: unknownReading.images.map((i) => i.imageBase64) },
        }),
    );
    const unknownReadingRef = useRef(unknownReading);
    useEffect(() => {
        unknownReadingRef.current = unknownReading;
    }, [unknownReading]);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const isUnknownReadingsEnabled = useSelector(applicationSelectors.isUnknownReadingsEnabled);
    const isUnknownReadingsImageRequired = useSelector(applicationSelectors.isUnknownReadingsImageRequired);
    const unknownReadingsMaxDaysInPast = useSelector(applicationSelectors.unknownReadingsMaxDaysInPast);
    const unknownReadingsMaxDaysInFuture = useSelector(applicationSelectors.unknownReadingsMaxDaysInFuture);
    const isQueryPostDecimalsEnabled = useSelector(applicationSelectors.isQueryPostDecimalsEnabled);
    const isMaskPostDecimalsEnabled = useSelector(applicationSelectors.isMaskPostDecimalsEnabled);

    const aiImageEvaluationEnabled = useSelector(applicationSelectors.getAiImageEvaluationEnabled);
    const aiImageEvaluationAllowDesktop = useSelector(applicationSelectors.getAiImageEvaluationAllowDesktop);
    const aiImageEvaluationAllowGallery = useSelector(applicationSelectors.getAiImageEvaluationAllowGallery);
    const aiMeterNumberMatchTrailingCharsCount = useSelector(applicationSelectors.getAiImageEvaluationMeterNumberMatchTrailingCharsCount);

    const applicationName = useSelector(applicationSelectors.getApplicationName);
    const loginCounts = useSelector(loginSelectors.getLoginCount);
    const logins = useSelector(loginSelectors.getLogins);

    const loginDropdownValues = useMemo(() => {
        let values: any[] = [];
        logins.map(([key, login]) =>
            values.push({
                id: login.info?.id,
                value: key,
                name: login.info?.name || t('jobs.unknownMeterReading.loginDropdown.optionFallback'),
            }),
        );
        return values;
    }, [logins, t]);

    const photoFlow = useMemo(
        () =>
            resolveJobPhotoFlow({
                isMobile: isMobilePlatform,
                aiImageEvaluationEnabled,
                aiImageEvaluationAllowDesktop,
                aiImageEvaluationAllowGallery,
            }),
        [aiImageEvaluationEnabled, aiImageEvaluationAllowDesktop, aiImageEvaluationAllowGallery],
    );

    const readingImagePrimary = useMemo(
        () =>
            resolveReadingImagePrimary({
                useAi: photoFlow.useAi,
                hasReadingValue: !!unknownReading.readingValue,
                hasImages: unknownReading.images.length > 0,
                imageRequired: isUnknownReadingsImageRequired,
            }),
        [photoFlow.useAi, unknownReading.readingValue, unknownReading.images.length, isUnknownReadingsImageRequired],
    );

    const latestDisplayCrop = unknownReading.images[unknownReading.images.length - 1]?.displayCrop;

    const aiReadingImageConfig = useMemo(
        () => ({
            accountId: accountId || logins[0]?.[0],
            meterNumber: unknownReading.meterNumber,
            meterNumberMatchTrailingCharsCount: aiMeterNumberMatchTrailingCharsCount,
        }),
        [accountId, logins, unknownReading.meterNumber, aiMeterNumberMatchTrailingCharsCount],
    );

    const currentSnapshot = useMemo(
        () =>
            buildUnknownMeterReadingSnapshot({
                accountId,
                unknownReading: { ...unknownReading, images: unknownReading.images.map((i) => i.imageBase64) },
            }),
        [accountId, unknownReading],
    );
    const hasUnsavedChanges = isUnknownMeterReadingDirty(initialSnapshotRef.current, currentSnapshot);
    useUnsavedChangesGuard(hasUnsavedChanges, t('jobs.details.unsavedChanges.content'));

    const isValid: boolean = useMemo(() => {
        return (
            (isUnknownReadingsImageRequired ? unknownReading.images.length > 0 && unknownReading.images.length <= MAX_READING_IMAGES : true) &&
            unknownReading.meterNumber.length > 0 &&
            unknownReading.readingValue.length > 0 &&
            unknownReading.readingDate !== null &&
            accountId?.length > 0
        );
    }, [unknownReading, isUnknownReadingsImageRequired, accountId]);

    useEffect(() => {
        if (!isUnknownReadingsEnabled) {
            dispatch(push('/ablesung'));
            dispatch(
                notificationActions.showNotification(
                    t('jobs.unknownMeterReading.notAllowedNotification.title'),
                    t('jobs.unknownMeterReading.notAllowedNotification.message'),
                    NotificationType.ERROR,
                ),
            );
        }
    }, [isUnknownReadingsEnabled, t, dispatch]);

    useEffect(() => {
        if (loginCounts === 1) {
            for (const [selectedAccountId] of logins) {
                initialSnapshotRef.current = resolveUnknownMeterReadingBaselineAfterAccountAutoSelect({
                    initialSnapshot: initialSnapshotRef.current,
                    selectedAccountId,
                    unknownReading: {
                        ...unknownReadingRef.current,
                        images: unknownReadingRef.current.images.map((i) => i.imageBase64),
                    },
                });
                setAccountId(selectedAccountId);
            }
        }
    }, [logins, loginCounts]);

    const onReadingImageResultReceived = useCallback((result: RemoteResult | undefined, imageBase64: string) => {
        setUnknownReading((prev) => {
            const existingImages = prev.images ?? [];
            if (existingImages.some((i) => i.imageBase64 === imageBase64)) {
                return prev;
            }
            const nextImages = [...existingImages, { imageBase64, displayCrop: result?.detectedDisplayCropBase64 }].slice(0, MAX_READING_IMAGES);
            const detectedValue = result?.meterRates?.[0]?.readingValue;
            const detectedMeterNumber = result?.meterNumber;
            return {
                ...prev,
                images: nextImages,
                readingValue: detectedValue ?? prev.readingValue,
                meterNumber: prev.meterNumber || detectedMeterNumber || '',
            };
        });
    }, []);

    const onReadingImageDelete = useCallback((index: number) => {
        setUnknownReading((prev) => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index),
        }));
    }, []);

    const onReadingImageStandardChange = useCallback((images: string[] | undefined) => {
        if (images != null) {
            setUnknownReading((prev) => ({ ...prev, images: images.map((imageBase64) => ({ imageBase64 })) }));
        }
    }, []);

    return (
        <BasicScreen>
            <Toolbar showBackButton={true} title={t('jobs.unknownMeterReading.heading')} />
            <div>
                <p className={'text-md mb-4 text-gray-500'}>
                    <Trans i18nKey={'jobs.unknownMeterReading.description'}>
                        Gern können Sie Zählerstände für Zähler erfassen, welche nicht in dieser Ableseliste angefragt wurden. Diese zusätzlichen Stände werden
                        nach der Erfassung direkt an <strong>{transValue({ applicationName })}</strong> weitergeleitet. Bitte beachten Sie, dass diese zusätzlich erfassten
                        Stände nach der Weiterleitung hier nicht mehr angezeigt werden.
                    </Trans>
                </p>
            </div>
            <Card className={classNames('job-details-add-unknown', className)}>
                <LabeledList>
                    {loginCounts > 1 && (
                        <OptionalLabel label={`${t('management.accounts.title')} *`}>
                            <Dropdown
                                placeholder={t('jobs.unknownMeterReading.loginDropdown.placeholder')}
                                values={loginDropdownValues}
                                value={accountId}
                                onChange={(value) => {
                                    setAccountId(value);
                                }}
                            />
                        </OptionalLabel>
                    )}
                </LabeledList>
            </Card>
            <Card>
                <LabeledList>
                    <OptionalLabel label={`${t('jobs.details.labels.meterNumber')} *`}>
                        <div className={'w-full flex items-center md:w-max md:justify-start justify-center'}>
                            <Input
                                aria={{
                                    'aria-label': `${t('jobs.details.labels.meterNumber')}`,
                                    'aria-placeholder': '563783929',
                                }}
                                className={'job-details-add-unknown-meter-number-input whitespace-nowrap inline-block'}
                                placeholder={'563783929'}
                                value={unknownReading.meterNumber}
                                maxLength={20}
                                inputMode={'text'}
                                onChange={(value) => {
                                    setUnknownReading({
                                        ...unknownReading,
                                        meterNumber: value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase(),
                                    });
                                }}
                                onFocusChange={(focus) => {
                                    setHasFocus({ ...hasFocus, meterNumberInput: focus });
                                }}
                            />
                            <div className={'lg:ml-3 relative'}>
                                {!hasFocus.meterNumberInput && unknownReading.meterNumber && (
                                    <Icon path={Icon.Path.mdiCheck} className={'h-6 text-green-500 absolute left-1 -top-3'} />
                                )}
                            </div>
                        </div>
                    </OptionalLabel>
                    {photoFlow.useAi && (
                        <OptionalLabel label={`${t('jobs.details.labels.readingImage')} ${isUnknownReadingsImageRequired ? '*' : ''}`}>
                            <>
                                <JobDetailsReadingImageEntry
                                    flow={photoFlow}
                                    config={aiReadingImageConfig}
                                    images={unknownReading.images.map((i) => i.imageBase64)}
                                    primary={readingImagePrimary}
                                    text={t('jobs.details.buttons.uploadImage')}
                                    onStandardChange={onReadingImageStandardChange}
                                    onResultReceived={onReadingImageResultReceived}
                                    onDelete={onReadingImageDelete}
                                />
                                {latestDisplayCrop && (
                                    <div className={'w-full border rounded px-2 py-1 bg-gray-100 mt-2'}>
                                        <HorizontalBase64Image src={latestDisplayCrop} alt={'Bildausschnitt vom Display'} className={'rounded'} />
                                    </div>
                                )}
                            </>
                        </OptionalLabel>
                    )}
                    <OptionalLabel label={`${t('jobs.details.labels.reading')} *`} className={'flex items-center flex-wrap xl:flex-nowrap'}>
                        <div className={'w-full flex items-center md:w-max md:justify-start justify-center'}>
                            <MeterReadingInput
                                maskPostDecimals={isMaskPostDecimalsEnabled}
                                key={instanceKey}
                                preDecimals={9}
                                postDecimals={isQueryPostDecimalsEnabled ? 3 : 0}
                                value={unknownReading.readingValue}
                                onChange={(value) => {
                                    setUnknownReading({
                                        ...unknownReading,
                                        readingValue: value,
                                    });
                                }}
                                onFocusChange={(focus) => {
                                    setHasFocus({ ...hasFocus, readingValueInput: focus });
                                }}
                            />
                            <div className={'lg:ml-3 relative'}>
                                {!hasFocus.readingValueInput && unknownReading.readingValue && (
                                    <Icon
                                        aria={{ 'aria-label': 'valider Stand Icon' }}
                                        path={Icon.Path.mdiCheck}
                                        className={'h-6 text-green-500 absolute left-1 -top-3'}
                                    />
                                )}
                            </div>
                        </div>
                    </OptionalLabel>
                    {!photoFlow.useAi && (
                        <OptionalLabel label={`${t('jobs.details.labels.readingImage')} ${isUnknownReadingsImageRequired ? '*' : ''}`}>
                            <JobDetailsReadingImageEntry
                                flow={photoFlow}
                                config={aiReadingImageConfig}
                                images={unknownReading.images.map((i) => i.imageBase64)}
                                primary={readingImagePrimary}
                                text={t('jobs.details.buttons.uploadImage')}
                                onStandardChange={onReadingImageStandardChange}
                                onResultReceived={onReadingImageResultReceived}
                                onDelete={onReadingImageDelete}
                            />
                        </OptionalLabel>
                    )}
                    <OptionalLabel label={`${t('jobs.details.labels.readingDate')} *`}>
                        <DatePicker
                            value={unknownReading.readingDate}
                            minDate={dayjs().subtract(unknownReadingsMaxDaysInPast, 'days').startOf('day').toDate()}
                            maxDate={dayjs().add(unknownReadingsMaxDaysInFuture, 'days').endOf('day').toDate()}
                            onChange={(date) => {
                                setUnknownReading({
                                    ...unknownReading,
                                    readingDate: date,
                                });
                            }}
                        />
                    </OptionalLabel>
                    <OptionalLabel label={t('jobs.details.labels.readingHint')}>
                        <JobComment
                            key={instanceKey}
                            textOnly={true}
                            onChange={(text) => {
                                setUnknownReading((previousUnknownReading) => {
                                    if (previousUnknownReading.comment === text) {
                                        return previousUnknownReading;
                                    }

                                    return {
                                        ...previousUnknownReading,
                                        comment: text,
                                    };
                                });
                            }}
                        />
                    </OptionalLabel>
                    <OptionalLabel className={'lg:mt-0 mt-2 flex flex-col'}>
                        {!isValid && <p className={'text-sm text-gray-400 flex-end'}>{t('common.fillRequiredFields')}</p>}
                        <PopConfirmButton
                            primary={true}
                            block={true}
                            okText={t('jobs.details.send.confirm')}
                            cancelText={t('jobs.details.send.cancel')}
                            disabled={!isValid}
                            isConfirmed={isValid}
                            onClick={() => {
                                let result: UnknownMeterReadingResultType;

                                try {
                                    result = {
                                        meterNumber: unknownReading.meterNumber,
                                        readingValue: Number.parseFloat(unknownReading.readingValue),
                                        readingImages: unknownReading.images.map((i) => toReadingImagePayload(i.imageBase64)),
                                        readingDate: unknownReading.readingDate.toISOString(),
                                        comment: unknownReading.comment && unknownReading.comment,
                                    };
                                } catch (err) {
                                    if (err instanceof InvalidReadingImagePayloadError) {
                                        dispatch(
                                            notificationActions.showErrorNotification(
                                                t('jobs.details.notifications.imageFormatUnsupported.title'),
                                                t('jobs.details.notifications.imageFormatUnsupported.message'),
                                            ),
                                        );
                                        return;
                                    }

                                    throw err;
                                }

                                dispatch(unknownReadingActions.sendUnknownMeterReadingResult(result, accountId));

                                const resetUnknownReading = {
                                    meterNumber: '',
                                    readingValue: '',
                                    images: [],
                                    readingDate: dayjs().toDate(),
                                    comment: undefined,
                                };

                                setUnknownReading(resetUnknownReading);
                                initialSnapshotRef.current = buildUnknownMeterReadingSnapshot({
                                    accountId,
                                    unknownReading: resetUnknownReading,
                                });

                                setInstanceKey(instanceKey + 1);
                            }}
                        >
                            {t('jobs.details.buttons.send')}
                        </PopConfirmButton>
                    </OptionalLabel>
                </LabeledList>
            </Card>
        </BasicScreen>
    );
};

export default JobDetailsAddUnknown;
